import React from 'react';

import { HoldingsReport } from '@ndvr/reporting-api';

import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';
import { formatCurrency, formatPercent } from 'utils';

import { ALLOCATION_SEGMENT_COLORS } from '../../asset-allocation-presentation';
import { ALLOCATION_SEGMENT_TOOLTIPS, getAssetAllocation } from '../../holdings';
import { formatFullCurrency, negativeColorClass } from '../../reporting/format';
import { CalculationTooltip } from '../../reporting/HeroStatCard';
import {
    HEAD_CLASS,
    HEAD_ROW_CLASS,
    NUMERIC_CELL,
    RIGHT_HEAD_CLASS,
    TABLE_CARD_HEADER_CLASS,
    TABLE_CLASS,
} from '../../reporting/table-styles';

interface AssetAllocationProps {
    report?: HoldingsReport | null;
    isLoading?: boolean;
}

// Card title row, shared by the empty and filled states.
const Header = ({ children }: React.PropsWithChildren) => (
    <div className={cn(TABLE_CARD_HEADER_CLASS, 'justify-between')}>
        <h3 className="font-heading text-cocoa-1000 text-xl">Allocation by asset class</h3>
        {children}
    </div>
);

export const AssetAllocation = ({ report, isLoading }: AssetAllocationProps) => {
    if (isLoading) {
        return <div className="bg-cocoa-100 h-64 w-full animate-pulse rounded-2xl" />;
    }

    const { total, segments } = report ? getAssetAllocation(report) : { total: 0, segments: [] };

    if (total <= 0 || segments.length === 0) {
        return (
            <Card className="border-border overflow-hidden rounded-2xl border bg-white">
                <Header />
                <p className="text-cocoa-400 px-4 pb-4 text-sm lg:px-6 lg:pb-6">
                    No holdings to allocate for this scope.
                </p>
            </Card>
        );
    }

    const weightOf = (amount: number) => (amount / total) * 100;

    return (
        <Card className="border-border overflow-hidden rounded-2xl border bg-white">
            <Header>
                <span className="text-cocoa-400 text-xs">{formatCurrency(total)} consolidated</span>
            </Header>

            <div className="px-4 pb-4 lg:px-6 lg:pb-6">
                {/* Per the design: a 26px bar with each segment's weight printed inside it; hovering a
                    segment dims the others and shows its class, amount and weight in a dark tooltip. */}
                <div className="group flex h-6.5 w-full gap-0.5 overflow-hidden rounded-md">
                    {segments.map((segment) => (
                        <Tooltip key={segment.key} delayDuration={0}>
                            <TooltipTrigger asChild>
                                <div
                                    className={cn(
                                        'text-cocoa-900 flex items-center justify-center overflow-hidden text-[11px] font-semibold whitespace-nowrap tabular-nums transition-opacity group-hover:[&:not(:hover)]:opacity-55',
                                        ALLOCATION_SEGMENT_COLORS[segment.key],
                                    )}
                                    style={{ width: `${weightOf(segment.amount)}%` }}
                                >
                                    {/* Below 6% the segment is too narrow for the label, as in the design;
                                        the tooltip still has it. */}
                                    {weightOf(segment.amount) >= 6 ? formatPercent(weightOf(segment.amount), 1) : null}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent
                                side="top"
                                sideOffset={8}
                                className="border-cocoa-900 bg-cocoa-900 rounded-lg px-3.5 py-2.5 text-white"
                            >
                                <div className="text-sm font-semibold">{segment.label}</div>
                                <div className="text-cocoa-200 mt-0.5 text-[13px] tabular-nums">
                                    {formatFullCurrency(segment.amount)} · {formatPercent(weightOf(segment.amount), 1)}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </div>
            </div>

            <Table className={TABLE_CLASS}>
                <TableHeader>
                    <TableRow className={HEAD_ROW_CLASS}>
                        <TableHead className={HEAD_CLASS}>Asset Class</TableHead>
                        <TableHead className={RIGHT_HEAD_CLASS}>Current %</TableHead>
                        {/* Weight bar column — no header text per design. */}
                        <TableHead className={cn(HEAD_CLASS, 'w-1/3')} />
                        <TableHead className={RIGHT_HEAD_CLASS}>Current $</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {segments.map((segment) => {
                        const weight = weightOf(segment.amount);
                        return (
                            <TableRow key={segment.key}>
                                <TableCell className="text-cocoa-1000 text-sm font-semibold">
                                    <span className="flex items-center gap-2.5">
                                        <span
                                            className={cn(
                                                'inline-block h-2.5 w-2.5 shrink-0 rounded-sm',
                                                ALLOCATION_SEGMENT_COLORS[segment.key],
                                            )}
                                        />
                                        <CalculationTooltip text={ALLOCATION_SEGMENT_TOOLTIPS[segment.key]}>
                                            <span>{segment.label}</span>
                                        </CalculationTooltip>
                                    </span>
                                </TableCell>
                                <TableCell className={cn(NUMERIC_CELL, 'text-cocoa-900 font-medium')}>
                                    {formatPercent(weight, 1)}
                                </TableCell>
                                <TableCell>
                                    <div className="bg-cocoa-100 h-2 rounded-full">
                                        <div
                                            className={cn(
                                                'h-full rounded-full',
                                                ALLOCATION_SEGMENT_COLORS[segment.key],
                                            )}
                                            style={{ width: `${weight}%` }}
                                        />
                                    </div>
                                </TableCell>
                                <TableCell
                                    className={cn(NUMERIC_CELL, negativeColorClass(segment.amount) ?? 'text-cocoa-900')}
                                >
                                    {formatFullCurrency(segment.amount)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {/* Same tinted total row as the Performance by Portfolio table. The weights are
                        shares of `total`, so they always add up to 100%. */}
                    <TableRow className="bg-cocoa-100/40 hover:bg-cocoa-100/40">
                        <TableCell className="text-cocoa-1000 text-sm font-semibold">Total</TableCell>
                        <TableCell className={cn(NUMERIC_CELL, 'text-cocoa-1000 font-semibold')}>
                            {formatPercent(100, 1)}
                        </TableCell>
                        <TableCell />
                        <TableCell
                            className={cn(
                                NUMERIC_CELL,
                                'font-semibold',
                                negativeColorClass(total) ?? 'text-cocoa-1000',
                            )}
                        >
                            {formatFullCurrency(total)}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Card>
    );
};
